import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';
import { Crown, Star, Check, ArrowRight, Zap, Shield } from 'lucide-react';
import logger from '../utils/logger';

const Packages = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState(null);
  const [debugResponse, setDebugResponse] = useState(null);

  const visiblePackages = packages.filter(pkg => pkg.visible !== false);

  // Fetch packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setPackagesLoading(true);

        const response = await get(getFullUrl('packages'));

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setDebugResponse(data); // Store for debugging

        // Handle both API Gateway wrapped response and direct response
        let packagesData;
        if (data.body) {
          // API Gateway wrapped response
          const obj = JSON.parse(data.body);
          packagesData = obj.packages || [];
        } else if (data.packages) {
          // Direct response with packages property
          packagesData = data.packages;
        } else if (Array.isArray(data)) {
          // Direct array response
          packagesData = data;
        } else {
          packagesData = [];
        }



        // Sort packages by price (ascending)
        const sortedPackages = packagesData.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

        // Map API response to expected format
        const formattedPackages = sortedPackages.map(pkg => ({
          id: pkg.packageId,
          name: pkg.name,
          price: parseFloat(pkg.price),
          description: pkg.description || 'Choose this package',
          icon: pkg.packageId === 'PKG_PREMIUM' ? Crown :
            pkg.packageId === 'PKG_STANDARD' ? Star : Zap,
          color: pkg.packageId === 'PKG_PREMIUM' ? 'gold' :
            pkg.packageId === 'PKG_STANDARD' ? 'purple' : 'blue',
          features: pkg.features || [],
          popular: pkg.packageId === 'PKG_STANDARD',
          visible: pkg.packageId !== 'PKG_PREMIUM' // Hide Premium package for now, change to true/remove check to enable
        }));

        setPackages(formattedPackages);
      } catch (err) {
        logger.error('Error fetching packages:', err);
        setError(`Failed to load packages: ${err.message || 'Unknown error'}`);
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const handlePurchase = (pkg) => {
    // Create a clean package object without React components
    const cleanPackage = {
      id: pkg.id,
      name: pkg.name,
      price: pkg.price,
      description: pkg.description,
      color: pkg.color,
      features: pkg.features,
      popular: pkg.popular
    };

    // Navigate to purchase page with selected package
    navigate('/purchase', { state: { selectedPackage: cleanPackage } });
  };

  const getPackageStyles = (pkg) => {
    const baseStyles = "relative rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1";

    if (pkg.popular) {
      return `${baseStyles} border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white`;
    }

    const colorMap = {
      blue: 'border-blue-200 bg-gradient-to-br from-blue-50 to-white',
      gold: 'border-yellow-200 bg-gradient-to-br from-yellow-50 to-white'
    };

    return `${baseStyles} ${colorMap[pkg.color]}`;
  };

  const getButtonStyles = (pkg) => {
    const baseStyles = "w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2";

    if (pkg.popular) {
      return `${baseStyles} bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800`;
    }

    const colorMap = {
      blue: 'bg-blue-600 text-white hover:bg-blue-700',
      gold: 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700'
    };

    return `${baseStyles} ${colorMap[pkg.color]}`;
  };

  const Icon = ({ pkg }) => {
    // Debug: log the icon to see what we're getting

    const IconComponent = pkg.icon;
    if (!IconComponent) {
      return <div className="h-8 w-8 bg-gray-300 rounded-full"></div>;
    }
    return <IconComponent className={`h-8 w-8 ${pkg.popular ? 'text-purple-600' : pkg.color === 'blue' ? 'text-blue-600' : 'text-yellow-600'}`} />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 rounded-full">
              <Star className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Learning Package
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Unlock your full potential with our comprehensive exam preparation packages
          </p>
        </div>

        {/* Loading State */}
        {packagesLoading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading packages...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Package Cards */}
        {!packagesLoading && !error && (
          <div className={`grid grid-cols-1 gap-8 mb-12 ${visiblePackages.length === 1
            ? 'max-w-md mx-auto'
            : visiblePackages.length === 2
              ? 'md:grid-cols-2 max-w-4xl mx-auto'
              : 'md:grid-cols-3'
            }`}>
            {visiblePackages.map((pkg) => (
              <div key={pkg.id} className={getPackageStyles(pkg)}>
                {/* Popular Badge */}
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-1">
                      <Star className="h-4 w-4" />
                      <span>Most Popular</span>
                    </div>
                  </div>
                )}

                <div className="p-8">
                  {/* Header */}
                  <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                      <Icon pkg={pkg} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{pkg.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        ${pkg.price.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-8">
                    <ul className="space-y-3">
                      {pkg.features && pkg.features.map((feature, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Check className={`h-5 w-5 ${pkg.popular ? 'text-purple-600' : pkg.color === 'blue' ? 'text-blue-600' : 'text-yellow-600'} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handlePurchase(pkg)}
                    disabled={loading}
                    className={getButtonStyles(pkg)}
                  >
                    {loading && selectedPackage?.id === pkg.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Get Started</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}



        {/* Debug: Raw API Response - Only show on localhost */}
        {window.location.hostname === 'localhost' && debugResponse && (
          <div className="mt-8 bg-gray-900 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-yellow-300">Debug: Raw API Response</h4>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(debugResponse, null, 2));
                  alert('API response copied to clipboard!');
                }}
                className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded hover:bg-gray-600"
              >
                Copy to Clipboard
              </button>
            </div>
            <textarea
              readOnly
              value={JSON.stringify(debugResponse, null, 2)}
              rows={10}
              className="w-full bg-gray-800 text-green-400 font-mono text-xs p-2 rounded border border-gray-700 resize-none"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Packages;
